import type { Locale } from '@/lib/i18n/locales';
import { formatDate, formatNumber, translate } from '@/lib/i18n/runtime';
import type { TimeFormat } from '@/store/theme-store';
import { formatListClock, localDayDistance } from './conversation-list-presentation';

export interface SystemEventSource { message_type?: string; metadata?: unknown; content?: string | null }
export interface SystemEvent {
  event: 'assigned_agent' | 'assigned_team' | 'self_assigned' | 'resolved' | 'reopened' | 'unassigned' | 'muted' | 'unmuted' | 'human_handoff' | 'csat_response';
  actor: { kind: 'user' | 'system' | 'bot' | 'integration'; name?: string };
  target_name?: string;
  rating?: number;
  scale_max?: 5 | 10 | null;
  feedback?: string;
}
const events = new Set(['assigned_agent', 'assigned_team', 'self_assigned', 'resolved', 'reopened', 'unassigned', 'muted', 'unmuted', 'human_handoff', 'csat_response']);
const actors = new Set(['user', 'system', 'bot', 'integration']);
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

/** Only explicit server metadata is localized. Historical content, customer
 * messages and unknown versions stay literal; never infer an event from prose. */
export function readSystemEvent(message: SystemEventSource): SystemEvent | null {
  if (message.message_type !== 'system' || !record(message.metadata)) return null;
  const value = message.metadata.blaze_system_event;
  if (!record(value) || value.version !== 1 || typeof value.event !== 'string' || !events.has(value.event) || !record(value.actor)) return null;
  const actor = value.actor;
  if (typeof actor.kind !== 'string' || !actors.has(actor.kind) || (actor.name !== undefined && typeof actor.name !== 'string') || (actor.kind === 'user' && (typeof actor.name !== 'string' || !actor.name.trim()))) return null;
  if (value.target_name !== undefined && typeof value.target_name !== 'string') return null;
  if (value.event === 'csat_response') {
    if (value.scale_max != null && value.scale_max !== 5 && value.scale_max !== 10) return null;
    if (typeof value.rating !== 'number' || !Number.isInteger(value.rating) || value.rating < 1 || value.rating > (value.scale_max ?? 10) || (value.feedback !== undefined && typeof value.feedback !== 'string')) return null;
  } else if (value.rating !== undefined || value.scale_max !== undefined || value.feedback !== undefined) return null;
  return {
    event: value.event as SystemEvent['event'], actor: { kind: actor.kind as SystemEvent['actor']['kind'], ...(typeof actor.name === 'string' ? { name: actor.name } : {}) },
    ...(typeof value.target_name === 'string' ? { target_name: value.target_name } : {}),
    ...(typeof value.rating === 'number' ? { rating: value.rating } : {}),
    ...(value.event === 'csat_response' ? { scale_max: value.scale_max === 5 || value.scale_max === 10 ? value.scale_max : null } : {}),
    ...(typeof value.feedback === 'string' ? { feedback: value.feedback } : {}),
  };
}

export type EventPart = { text: string; literal: boolean; dir?: 'ltr' };
/** Fill trusted template slots once, as text nodes. Literal names containing
 * braces, markup or RTL text never become a second template or HTML. */
export function systemEventParts(event: SystemEvent, locale: Locale): EventPart[] {
  let key = `timeline.${event.event}`;
  if ((event.event === 'assigned_agent' || event.event === 'assigned_team') && !event.target_name) key += '_generic';
  if (event.event === 'csat_response' && event.scale_max == null) key += '_unknown_scale';
  if (event.event === 'csat_response' && event.feedback) key += '_feedback';
  const slots: Record<string, string> = {
    actor: event.actor.name || translate(`timeline.actor.${event.actor.kind}`, {}, 'inbox', locale),
    target: event.target_name ?? '',
    rating: event.rating === undefined ? '' : formatNumber(event.rating, {}, locale),
    scale: event.scale_max == null ? '' : formatNumber(event.scale_max, {}, locale),
    score: event.rating === undefined || event.scale_max == null ? '' : `${formatNumber(event.rating, {}, locale)}/${formatNumber(event.scale_max, {}, locale)}`,
    feedback: event.feedback ?? '',
  };
  // Isolate the complete ratio, so RTL cannot visually swap numerator/denominator.
  // Only trusted catalog tokens are grouped; literal names/feedback are filled once.
  return translate(key, {}, 'inbox', locale).replace('{rating}/{scale}', '{score}').split(/(\{(?:actor|target|rating|scale|score|feedback)\})/g).filter(Boolean).map(part => {
    const slot = part.match(/^\{(actor|target|rating|scale|score|feedback)\}$/)?.[1];
    return slot ? { text: slots[slot], literal: true, ...(slot === 'score' ? {dir: 'ltr' as const} : {}) } : { text: part, literal: false };
  });
}

/** Group dates in the same local civil zone in which their clocks are shown. */
export function timelineDayKey(iso: string | undefined): string {
  const date = iso ? new Date(iso) : null;
  if (!date || !Number.isFinite(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
export function formatTimelineDay(key: string, locale: Locale, now = new Date()): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return '';
  const date = new Date(`${key}T12:00:00`);
  if (!Number.isFinite(date.getTime()) || timelineDayKey(date.toISOString()) !== key) return '';
  const distance = localDayDistance(date, now);
  const civil = formatDate(date, { day: 'numeric', month: 'short', ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' as const } : {}) }, locale);
  const label = distance === 0 ? translate('date.today', {}, 'inbox', locale) : distance === 1 ? translate('date.yesterday', {}, 'inbox', locale) : formatDate(date, { weekday: 'short' }, locale);
  return `${label} · ${civil}`.toLocaleUpperCase(locale);
}
export function formatTimelineClock(iso: string | undefined, preference: TimeFormat, locale: Locale): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isFinite(date.getTime()) ? formatListClock(date, preference, locale) : '';
}
export function formatTimelineTimestamp(iso: string, preference: TimeFormat, locale: Locale): string | undefined {
  const date = new Date(iso);
  return Number.isFinite(date.getTime()) ? formatDate(date, { dateStyle: 'medium', timeStyle: 'short', hourCycle: preference === '24h' ? 'h23' : 'h12' }, locale) : undefined;
}
